// @ts-nocheck
import { Component } from '@theme/component';

/**
 * Parse a money string (e.g. "$12.00") into a float
 * @param {string} str
 * @returns {number}
 */
function parseMoneyString(str) {
  if (typeof str !== 'string') return 0;
  const cleaned = str.replace(/[^\d.,\-]/g, '');
  if (!cleaned) return 0;
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized = cleaned;
  if (lastComma !== -1 && lastDot !== -1) {
    normalized = lastComma > lastDot ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '');
  } else if (lastComma !== -1) {
    normalized = cleaned.replace(',', '.');
  }
  const result = parseFloat(normalized);
  return isNaN(result) ? 0 : result;
}

/**
 * Format cents to money string, falling back to a simple "$X.XX" format.
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  if (window.formatMoneyWithCurrency) {
    return window.formatMoneyWithCurrency(cents, { enableConversion: false });
  }
  return '$' + (cents / 100).toFixed(2);
}

/**
 * Calculate the discount amount in cents from a `data-discount-text` value.
 * The format is `p{X}` where X is the dollar value.
 * @param {Element} container
 * @returns {number} discount in cents
 */
function getDiscountAmountCents(container) {
  const el = container.querySelector('[data-discount-text]');
  if (!el) return 0;
  const match = (el.dataset.discountText || '').match(/p\{(\d+)\}/);
  if (!match) return 0;
  const num = parseInt(match[1], 10);
  if (isNaN(num)) return 0;
  if (window.formatMoneyWithCurrency && window.parseMoneyString) {
    const parsed = window.parseMoneyString(window.formatMoneyWithCurrency(num * 100));
    return isNaN(parsed) ? num * 100 : parsed * 100;
  }
  return num * 100;
}

/**
 * Apply a discount (in cents) to a price string, returning the discounted price string.
 * @param {string} priceStr
 * @param {number} discountCents
 * @returns {string}
 */
function applyDiscount(priceStr, discountCents) {
  const originalCents = Math.round(parseMoneyString(priceStr) * 100);
  const discountedCents = Math.max(0, originalCents - discountCents);
  return formatMoney(discountedCents);
}

export default class ModuleLoginSale extends Component {
  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#handleClick.bind(this), { signal });
    this.#bindSelectComponents();

    this.#initDiscountText();
    // Defer price adjustment until currency helpers are ready
    setTimeout(() => this.#applyInitialDiscounts(), 100);
  }

  /** Bind select-change directly on each select-component (event does not bubble) */
  #bindSelectComponents() {
    const handler = this.#handleSelectChange.bind(this);
    const { signal } = this.#abort;
    this.querySelectorAll('select-component').forEach((el) => {
      el.addEventListener('select-change', handler, { signal });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  // ─── Event delegation ────────────────────────────────────────────────────

  /**
   * @param {MouseEvent} e
   */
  #handleClick(e) {
    const mediaToggle = /** @type {Element} */ (e.target).closest('.media-toggle');
    if (mediaToggle) {
      this.#handleMediaToggle(mediaToggle);
      return;
    }

    const copyBtn = /** @type {Element} */ (e.target).closest('.copy-coupon-btn');
    if (copyBtn) {
      this.#handleCopy(copyBtn);
    }
  }

  /**
   * @param {CustomEvent} e
   */
  #handleSelectChange(e) {
    const { data } = e.detail;
    const productContainer = /** @type {Element} */ (e.target).closest('.sv1-pd');
    if (!productContainer) return;

    const discountCents = getDiscountAmountCents(productContainer);
    const discountPct = Number(data.discountPercentage);

    // Price
    this.#updatePrice(productContainer, data.price, data.comparePrice, discountPct, discountCents);

    // Variant image + badge
    const imageContainer = productContainer.querySelector('.variant-image-container');
    if (data.image) {
      const img = imageContainer?.querySelector('.variant-image');
      if (img) {
        img.src = data.image;
        img.srcset = data.image;
      }
    }
    this.#updateDiscountBadge(imageContainer, discountPct);

    // Add-to-cart thumbnail
    const addToCartComp = productContainer.querySelector('add-to-cart-component');
    if (addToCartComp && data.image) addToCartComp.dataset.productVariantMedia = data.image;

    // Product form hidden input
    const hiddenInput = productContainer.querySelector('product-form-component input[name="id"]');
    if (hiddenInput) hiddenInput.value = data.variantId;

    // Payment terms
    const paymentInput = productContainer.querySelector('payment-terms input[name="id"]');
    const paymentTerms = productContainer.querySelector('shopify-payment-terms');
    if (paymentInput) paymentInput.value = data.variantId;
    if (paymentTerms) paymentTerms.setAttribute('variant-id', data.variantId);
  }

  // ─── Media toggle ─────────────────────────────────────────────────────────

  /**
   * @param {Element} toggle
   */
  #handleMediaToggle(toggle) {
    const targetType = toggle.dataset.target;
    const container = toggle.closest('.relative');
    if (!container) return;

    container.querySelectorAll('.media-content').forEach((content) => {
      const isTarget = content.dataset.type === targetType;
      content.classList.toggle('hidden', !isTarget);
      content.classList.toggle('active', isTarget);

      if (content.dataset.type === 'video') {
        const video = content.querySelector('video');
        if (!video) return;
        if (isTarget) {
          setTimeout(() => video.play().catch(() => {}), 100);
        } else {
          video.pause();
        }
      }
    });

    container.querySelectorAll('.media-toggle').forEach((btn) => {
      const isActive = btn.dataset.target === targetType;
      btn.classList.toggle('!bg-[#1C3C57]', isActive);
      btn.classList.toggle('!text-white', isActive);
      btn.classList.toggle('!bg-gray-100', !isActive);
      btn.classList.toggle('!text-gray-600', !isActive);
    });
  }

  // ─── Coupon copy ─────────────────────────────────────────────────────────

  /**
   * @param {Element} btn
   */
  async #handleCopy(btn) {
    const code = btn.dataset.couponCode;
    if (!code) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        this.#fallbackCopy(code);
      }
      this.#showCopyFeedback(btn);
    } catch {
      this.#fallbackCopy(code);
      this.#showCopyFeedback(btn);
    }
  }

  /**
   * @param {string} text
   */
  #fallbackCopy(text) {
    const area = Object.assign(document.createElement('textarea'), {
      value: text,
    });
    Object.assign(area.style, { position: 'fixed', left: '-9999px', top: '-9999px' });
    document.body.appendChild(area);
    area.focus();
    area.select();
    document.execCommand('copy');
    document.body.removeChild(area);
  }

  /**
   * @param {Element} btn
   */
  #showCopyFeedback(btn) {
    const span = btn.querySelector('span');
    if (!span) return;
    const original = span.textContent;
    span.textContent = 'Copied!';
    btn.classList.add('text-green-600');
    setTimeout(() => {
      span.textContent = original;
      btn.classList.remove('text-green-600');
    }, 2000);
  }

  // ─── Price display ────────────────────────────────────────────────────────

  /**
   * @param {Element} container
   * @param {string} price
   * @param {string} comparePrice
   * @param {number} discountPct
   * @param {number} discountCents
   */
  #updatePrice(container, price, comparePrice, discountPct, discountCents) {
    const priceContainer = container.querySelector('.price-container');
    if (!priceContainer) return;

    const finalPrice = discountCents > 0 ? applyDiscount(price, discountCents) : price;

    if (comparePrice && comparePrice !== price && discountPct > 0) {
      priceContainer.innerHTML = `
        <div class="flex items-center gap-3 flex-wrap">
          <span>Discount unlocked, You pay</span>
          <span class="current-price text-xl md:text-2xl font-bold text-red-600">${finalPrice}</span>
          <span class="compare-price text-base md:text-lg text-gray-500 line-through">${comparePrice}</span>
        </div>`;
    } else {
      priceContainer.innerHTML = `
        <div class="flex items-center gap-3 flex-wrap">
          <span>Discount unlocked, You pay</span>
          <div class="current-price text-xl md:text-2xl font-bold text-gray-900">${finalPrice}</div>
        </div>`;
    }
  }

  /**
   * @param {Element | null} imageContainer
   * @param {number} discountPct
   */
  #updateDiscountBadge(imageContainer, discountPct) {
    if (!imageContainer) return;
    let badge = imageContainer.querySelector('.discount-badge-sale');
    if (discountPct > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className =
          'discount-badge-sale absolute top-2 left-2 bg-red-100 text-[#E74C3C] px-4 py-1 rounded-full text-xs md:text-sm font-bold z-1';
        imageContainer.appendChild(badge);
      }
      badge.textContent = `-${discountPct}%`;
      badge.style.display = '';
    } else if (badge) {
      badge.style.display = 'none';
    }
  }

  // ─── Initial setup ────────────────────────────────────────────────────────

  /** Render `p{X}` placeholders in discount text elements */
  #initDiscountText() {
    this.querySelectorAll('[data-discount-text]').forEach((el) => {
      const raw = el.dataset.discountText;
      if (!raw || !window.formatMoneyWithCurrency) return;
      el.textContent = raw.replace(/p\{(\d+)\}/g, (_, n) =>
        window.formatMoneyWithCurrency(Number(n) * 100, { enableConversion: true }),
      );
    });
  }

  /** Apply coupon discount to initial rendered prices */
  #applyInitialDiscounts() {
    this.querySelectorAll('.sv1-pd').forEach((container) => {
      const priceEl = container.querySelector('.price-container .current-price');
      if (!priceEl) return;

      const discountCents = getDiscountAmountCents(container);
      if (discountCents <= 0) return;

      const discountedPrice = applyDiscount(priceEl.textContent.trim(), discountCents);
      priceEl.textContent = discountedPrice;

      // Recalculate badge percentage after coupon discount
      const compareEl = container.querySelector('.price-container .compare-price');
      if (compareEl) {
        const cp = parseMoneyString(compareEl.textContent.trim());
        const pp = parseMoneyString(discountedPrice);
        if (cp > pp) {
          this.#updateDiscountBadge(
            container.querySelector('.variant-image-container'),
            Math.round(((cp - pp) / cp) * 100),
          );
        }
      }
    });
  }
}

customElements.define('module-login-sale', ModuleLoginSale);
